import Vue from 'vue';
import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';
import { MUTATIONS, _SCHOOL_CALENDAR_STORE } from '../values/school-calendar.values';
import { SchoolCalendarService } from '../services/school-calendar.service';
import { IRequestSaveHoliday } from '../interfaces/request-save-holiday.interface';
import { IRequestSaveWorkingDays } from '../interfaces/request-save-workingdays.interface';
import { IRequestDeleteHoliday } from '../interfaces/request-delete-holiday.interface';
import { IRequestDeleteSchoolDay } from '../interfaces/request-delete-schoolday.interface';
import { SchoolDay } from '../models/school-day.model';
import { Holiday } from '../models/holiday.model';
import { WEEK_DAYS } from '@/values/general.values';
import { ISchoolCalendarPreferences } from '../interfaces/school-calendar-preferences.interface';
import { SchoolDays } from '@/tools/models/school-days.model';
import { OrganizationSchoolDays } from '../models/organization-school-days.model';

export const SCHOOL_CALENDAR_STORE = _SCHOOL_CALENDAR_STORE;

@Module({ namespaced: true })
export class SchoolCalendarStore extends VuexModule {

  /* State */
  public schooldays: SchoolDay[] = []; 
  public schooldaysActive: any[] = [];
  public schoolDaySelected: any[];
  public schooldaysList: SchoolDays = {
    dates: [],
    schoolDayId: null
  }
  public organizationSchoolDays: OrganizationSchoolDays[] = [];
  public workingDays: any[] = [];
  public holidays: Holiday[] = [];
  public showNonSchoolDays: boolean = false;
  public preferences: ISchoolCalendarPreferences = {
    ownerId: 0,
    ownerType: '',
    colors: {
      CURRENT_DATE: '#2BB2AC',
      US_HOLIDAYS: '#FB3640',
      SCHOOL_DAYS: '#30bdb6',
      EVENTS: '#FFBB00',
      DAY_OFF: '#696969'
    }
  };

  /* Mutations */
  @Mutation
  public setWorkingDays(data: any[]) {
    Vue.set(this, 'workingDays', data);
  }

  @Mutation
  public setHolidays(data: any[]) {
    Vue.set(this, 'holidays', data);
  }

  @Mutation
  public setSchooldays(data: any[]) {
    Vue.set(this, 'schooldays', data);
  }

  @Mutation
  public updateSchoolDayColor(schoolDay: SchoolDay) {
    let i = this.schooldays.findIndex(sd => sd.schoolDayId == schoolDay.schoolDayId);
    if(i !== -1) Vue.set(this.schooldays[i], 'color', schoolDay.color);
  }

  @Mutation
  public setSchooldaysActive(data: any[]) {
    Vue.set(this, 'schooldaysActive', data);
  }

  @Mutation
  public setSchooldaysList(data: any[]) {
    Vue.set(this, 'schooldaysList', data);
  }

  @Mutation
  public setPreferences(data: ISchoolCalendarPreferences) {
    Vue.set(this, 'preferences', data);
  }

  @Mutation
  public updateColor({ key, value }) {
    Vue.set(this.preferences.colors, key, value);
  }

  @Mutation
  public updateShowNonSchoolDays(value: boolean) {
    this.showNonSchoolDays = value;
  }

  @Mutation
  public setOrganizationSchooldaysList({ data, organizationId}) {
    let i = this.organizationSchoolDays
      .findIndex(o => o.organizationId == organizationId);
    let toSave = {
      ...data,
      organizationId
    };
    if(i == -1 && organizationId) {
      this.organizationSchoolDays.push(new OrganizationSchoolDays(toSave));
    } else {
      this.organizationSchoolDays[i] = toSave;
    }
  }

  /* Actions */
  @Action({ rawError: true })
  public getWorkingDays({ organizationId }: { organizationId: number }): Promise<void> {
    return new Promise((resolve, reject) => {
      SchoolCalendarService.workingDays(organizationId)
        .then((res: any) => {
          let data = [];
          for (let dayOfWeek of WEEK_DAYS) {
            data.push({
              label: dayOfWeek,
              checked: res.includes(dayOfWeek.toUpperCase())
            });
          }
          this.context.commit(MUTATIONS.SET_WORKING_DAYS, data);
          resolve(res);
        }).catch(err => {
          reject(err);
        });
    });
  }

  @Action({ rawError: true })
  public getHolidays({ organizationId, year, startDate, endDate }:
    { organizationId: number, year: string, startDate: string, endDate: string }): Promise<void> {
    return new Promise((resolve, reject) => {
      SchoolCalendarService.holidays(organizationId, year, startDate, endDate)
        .then((res: any) => {
          this.context.commit(MUTATIONS.SET_HOLIDAYS, res);
          resolve(res);
        }).catch(err => {
          reject(err);
        });
    });
  }

  @Action({ rawError: true })
  public getSchooldays({ organizationId, year, startDate, endDate }:
    { organizationId: number, year: string, startDate: string, endDate: string }): Promise<void> {
    return new Promise((resolve, reject) => {
      SchoolCalendarService.schooldays(organizationId, year, startDate, endDate)
        .then((res: any) => {
          this.context.commit(MUTATIONS.SET_SCHOOLDAYS, res);
          resolve(res);
        }).catch(err => {
          reject(err);
        });
    });
  }

  @Action({ rawError: true })
  public getSchooldayById(shooldayId : number): Promise<void> {
    return new Promise((resolve, reject) => {
      SchoolCalendarService.getSchoolCalendarById(shooldayId)
        .then((res: any) => {
          this.context.commit(MUTATIONS.SET_SCHOOLDAYS, [res]);
          resolve(res);
        }).catch(err => {
          reject(err);
        });
    });
  }

  @Action({ rawError: true })
  public getSchooldaysActive({ organizationId }:
    { organizationId: number }): Promise<void> {
    return new Promise((resolve, reject) => {
      SchoolCalendarService.schooldaysActive(organizationId)
        .then((res: any) => {
          this.context.commit(MUTATIONS.SET_SCHOOLDAYS_ACTIVE, res);
          resolve(res);
        }).catch(err => {
          reject(err);
        });
    });
  }

  @Action({ rawError: true })
  public getSchooldaysList({ organizationId }:
    { organizationId: number }): Promise<void> {
    return new Promise((resolve, reject) => {
      SchoolCalendarService.schooldaysList(organizationId)
        .then((res: any) => {
          this.context.commit(MUTATIONS.SET_SCHOOLDAYS_LIST, res);
          resolve(res);
        }).catch(err => {
          reject(err);
        });
    });
  }

  @Action({ rawError: true })
  public getColorsPreferences(organizationId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      SchoolCalendarService.getColorPreferences(organizationId)
        .then((preferences: ISchoolCalendarPreferences) => {
          this.context.commit(MUTATIONS.SET_PREFERENCES, preferences);
          resolve();
        }).catch(err => {
          reject(err);
        });
    });
  }


  @Action({ rawError: true })
  public saveWorkingDays(data: IRequestSaveWorkingDays): Promise<void> {
    return SchoolCalendarService.saveWorkingDays(data);
  }

  @Action({ rawError: true })
  public saveSchooldays(schoolday: SchoolDay): Promise<void> {
    return new Promise((resolve, reject) => {
      SchoolCalendarService.saveSchooldays(schoolday)
        .then((res: any) => {
          this.context.commit(MUTATIONS.UPDATE_SCHOOL_DAY_COLOR, schoolday);
          resolve(res);
        }).catch(err => {
          reject(err);
        });
    });
  }


  @Action({ rawError: true })
  public saveHolidays(holiday: IRequestSaveHoliday): Promise<void> {
    return new Promise((resolve, reject) => {
      SchoolCalendarService.saveHolidays(holiday)
        .then((res: any) => {
          resolve(res);
        }).catch(err => {
          reject(err);
        });
    });
  }

  @Action({ rawError: true })
  public saveColorsPreferences({ key, value }): Promise<void> {
    return new Promise((resolve, reject) => {
      let req = { ...this.preferences };
      req.colors[key] = value;
      SchoolCalendarService.saveColorsPreference(req)
        .then(() => {
          this.context.commit(MUTATIONS.UPDATE_COLOR, { key, value });
          resolve();
        }).catch(err => {
          reject(err);
        });
    });
  }

  @Action({ rawError: true })
  public deleteHolidays(holiday: IRequestDeleteHoliday): Promise<void> {
    return new Promise((resolve, reject) => {
      SchoolCalendarService.deleteHolidays(holiday)
        .then((res: any) => {
          resolve(res);
        }).catch(err => {
          reject(err);
        });
    });
  }

  @Action({ rawError: true })
  public deleteSchooldays(schoolday: IRequestDeleteSchoolDay): Promise<void> {
    return new Promise((resolve, reject) => {
      SchoolCalendarService.deleteSchoolDay(schoolday)
        .then((res: any) => {
          resolve(res);
        }).catch(err => {
          reject(err);
        });
    });
  }

  @Action({ rawError: true })
  public getOrganizationSchooldaysList(organizationId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      SchoolCalendarService.schooldaysList(organizationId)
        .then((res: any) => {
          this.context.commit(MUTATIONS.SET_ORGANIZATION_SCHOOL_DAYS_LIST, {
            data: res,
            organizationId
          });
          resolve(res);
        }).catch(err => {
          reject(err);
        });
    });
  }

  /* Initial State */
  @Mutation
  public initState(state) {
    this.schooldays = []; 
    this.schooldaysActive = [];
    this.schoolDaySelected = null;
    this.schooldaysList = {
      dates: [],
      schoolDayId: null
    }
    this.organizationSchoolDays = [];
    this.workingDays = [];
    this.holidays = [];
    this.showNonSchoolDays = false;
    this.preferences = {
      ownerId: 0,
      ownerType: '',
      colors: {
        CURRENT_DATE: '#2BB2AC',
        US_HOLIDAYS: '#FB3640',
        SCHOOL_DAYS: '#30bdb6',
        EVENTS: '#FFBB00',
        DAY_OFF: '#696969'
      }
    };
  }

}
